// file: src/utils/sseUtils.js

/**
 * Helper function to process server-sent events (SSE) stream
 * @param {Response} response - Fetch API response object with body stream
 * @param {Function} onPartial - Callback for each parsed SSE event
 * @returns {Promise<any>} - Returns the final result (last event data or null)
 */
export async function streamSse(response, onPartial) {
  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finalResult = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed.startsWith('data:') || trimmed === 'data:[DONE]') {
        if (trimmed === 'data:[DONE]') {
          // Stream is finished
          return finalResult;
        }
        continue;
      }

      try {
        const jsonText = trimmed.replace('data:', '').trim();
        const eventData = JSON.parse(jsonText);
        
        // Keep track of final result
        finalResult = eventData;
        
        // Call the callback with parsed data
        if (onPartial) onPartial(eventData);
      } catch (err) {
        console.error('Error parsing SSE chunk:', err, 'Raw text:', trimmed);
      }
    }
  }

  return finalResult;
}

/**
 * Create a fetch request configured for SSE streaming
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch Response 
 */
export async function createSseRequest(url, options = {}) {
  const defaultOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    credentials: 'include', // Include cookies
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  return fetch(url, mergedOptions);
}

/**
 * Complete function to handle SSE fetch and streaming
 * @param {string} url - The URL to fetch
 * @param {Object} options - Fetch options including body
 * @param {Function} onPartial - Callback for partial results
 * @returns {Promise<any>} - Final result
 */
export async function fetchSse(url, options, onPartial) {
  const response = await createSseRequest(url, options);
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`SSE request failed: ${response.status} ${response.statusText}\n${errorText}`);
  }
  
  return streamSse(response, onPartial);
} 